import React from 'react';
import styled from 'styled-components';

import { LoadingButton } from '@common/Button';
import { LayoutSidebar, LayoutWorkspace } from '@common/layout';
import { useParams, useNavigate } from 'react-router-dom';
import { ServiceNavigation } from '@components/Workspace/Navigation';
import { useSelector } from 'react-redux';
import { State } from '@store/index';
import { Delete } from '@mui/icons-material';
import { FileSidebar } from './FileSidebar';
import { TextFilePreview } from './FilePreview';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteObjectsFromBucket, getBucket, getBucketFile } from '@requests/buckets/buckets.requests';
import { Alert } from '@mui/material';
import { CodePreview } from './FilePreview/CodePreview';
import { PDFPreview } from './FilePreview/PDFPreview';
import { Resource } from '@store/types';
import { usePermissions } from '@common/hooks/usePermissions';
import { enqueueSnackbar } from 'notistack';
import { GetBucketFileParams, TelnyxBucketFileContent } from '@requests/buckets';
import { RequestError } from '@requests/common';

const Container = styled.div`
  display: flex;
  height: calc(100vh - 61px);
`;

const ErrorContainer = styled.div`
  width: 100%;
  padding: 2rem;
`;

export const File: React.FC = () => {
  const { service_id, bucketName, fileName } = useParams();
  const { admin, permissions } = usePermissions(service_id as string, Resource.BUCKET);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const services = useSelector((state: State) => state.flow.services);
  const service = services.find((service) => service.service_id === service_id);
  const allowDelete = admin || permissions?.delete;

  const { data: bucket = null } = useQuery({
    queryKey: ['globalFiles', 'bucket', { service_id, bucket_name: bucketName }],
    queryFn: getBucket,
    enabled: Boolean(service_id && bucketName),
  });

  const {
    data: file = null,
    error: fileError,
    isLoading,
  } = useQuery<TelnyxBucketFileContent, RequestError, TelnyxBucketFileContent, [string, GetBucketFileParams]>({
    queryKey: [
      'file',
      {
        service_id: service_id as string,
        bucket_name: bucketName as string,
        file_name: fileName as string,
        region: bucket?.region as string,
      },
    ],
    retry: false,
    queryFn: getBucketFile,
    enabled: Boolean(bucket?.region && bucketName && fileName),
  });

  const {
    mutate: deleteFile,
    isPending: isFileDeleting,
    error: deleteError,
  } = useMutation({
    mutationFn: deleteObjectsFromBucket,
    onSuccess: (data) => {
      if (data?.error) {
        enqueueSnackbar(data?.error || 'An unexpected error occured.', {
          variant: 'error',
          autoHideDuration: 10000,
        });

        enqueueSnackbar('Failed to delete file..', {
          variant: 'error',
          autoHideDuration: 10000,
        });

        return;
      }

      // Show success notification before navigation
      enqueueSnackbar(`File "${fileName}" was successfully deleted`, {
        variant: 'success',
        autoHideDuration: 3000,
        preventDuplicate: true,
      });

      // Only invalidate the files query for this specific bucket
      queryClient.invalidateQueries({
        queryKey: ['globalFiles', 'files', { service_id, bucketName, region: bucket?.region }],
      });

      navigate(`/${service_id}/buckets/${bucketName}`);
    },
    onError: (error) => {
      // Show error notification
      let errorMessage = 'Failed to delete file';
      if (error instanceof Error) {
        errorMessage = `Failed to delete file: ${error.message}`;
      }

      enqueueSnackbar(errorMessage, {
        variant: 'error',
        autoHideDuration: 5000,
        preventDuplicate: true,
      });
    },
  });

  const error = fileError || deleteError;

  const isMarkdown = file?.content_type?.includes('text') || file?.content_type?.includes('octet-stream');
  const isCodePreview = file?.content_type === 'application/json';
  const isPdf = file?.content_type === 'application/pdf';

  const handleDelete = () => {
    if (!bucket?.region || !fileName || !bucketName || !service_id) return;

    // Show a notification immediately when delete is clicked
    enqueueSnackbar(`Deleting file "${fileName}"`, {
      variant: 'info',
      autoHideDuration: 2000,
      preventDuplicate: true,
    });

    try {
      deleteFile({
        service_id: service_id,
        bucketName: bucketName,
        region: bucket.region,
        objectNames: [fileName],
      });
    } catch {}
  };

  return (
    <LayoutWorkspace
      header={service?.service_name || 'Workspace'}
      body={
        <Container>
          <ServiceNavigation />
          <LayoutSidebar
            body={
              <>
                {fileError && (
                  <ErrorContainer>
                    <Alert severity='error'>{fileError?.response?.data?.error}</Alert>
                  </ErrorContainer>
                )}
                {isMarkdown && !error && file && <TextFilePreview isLoading={isLoading} file={file.file} />}
                {isCodePreview && !error && <CodePreview isLoading={isLoading} file={file} />}
                {isPdf && !error && <PDFPreview isLoading={isLoading} file={file} />}
              </>
            }
            leftSidebar={
              <FileSidebar
                file={file?.file}
                isLoading={isLoading}
                contentType={file?.content_type || ''}
                error={error}
              />
            }
          />
        </Container>
      }
      actions={
        <LoadingButton
          customColor='black'
          startIcon={<Delete />}
          loading={isFileDeleting}
          disabled={Boolean(error) || !allowDelete}
          onClick={handleDelete}
          minHeight='32px'
          minWidth='128px'
        >
          Delete File
        </LoadingButton>
      }
    />
  );
};
